import React, { useState } from "react";
import { apiFetchJson } from "../api";
import {
  PlusIcon,
  RefreshIcon,
  SendIcon,
  CheckIcon,
  XIcon,
  GlobeIcon,
  MapPinIcon,
  BuildingIcon,
  Spinner
} from "./Icons";
import { t, tf } from "../i18n";
import { A, aa, S, onAccent } from "../theme/palette";

/**
 * InlineAddProxy — добавление прокси прямо в разделе «Прокси», без модалки.
 *
 * Интеграция (frontend/src/App.jsx, компонент ProxiesTab):
 *   1. import InlineAddProxy from "./components/InlineAddProxy";
 *   2. Убрать из шапки секции кнопку «Добавить прокси» (setShowAddProxyModal),
 *      оставив только «Проверить все».
 *   3. Сразу под section-header отрисовать:
 *        <InlineAddProxy
 *          isPhoneView={isPhoneView}
 *          onSuccess={(proxy) => {
 *            setProxies((prev) => orderProxiesBuiltinFirst([...prev, proxy]));
 *          }}
 *        />
 *   4. Модалку <AddProxyModal /> и стейт showAddProxyModal можно удалить.
 *
 * Тип фиксирован SOCKS5 (используется только он). Поле «Адрес» распознаёт
 * вставленную целиком строку в форматах:
 *   socks5://user:pass@host:port  ·  host:port:user:pass  ·  host:port
 */

// Разбор полной строки прокси в части. Возвращает частичный
// { host, port, username, password } или null для «голого» хоста.
const parseProxyString = (raw) => {
  let s = String(raw || "").trim();
  if (!s) return null;
  // срезаем схему (socks5://, socks4://, http://, https://, socks://)
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");

  // credentials@host:port
  if (s.includes("@")) {
    const at = s.lastIndexOf("@");
    const cred = s.slice(0, at);
    const hp = s.slice(at + 1);
    const ci = cred.indexOf(":");
    const username = ci >= 0 ? cred.slice(0, ci) : cred;
    const password = ci >= 0 ? cred.slice(ci + 1) : "";
    const [host, port] = hp.split(":");
    return { host: host || "", port: port || "", username, password };
  }

  // разделение по двоеточиям
  const parts = s.split(":");
  if (parts.length >= 4) {
    return { host: parts[0], port: parts[1], username: parts[2], password: parts.slice(3).join(":") };
  }
  if (parts.length === 3) {
    return { host: parts[0], port: parts[1], username: parts[2], password: "" };
  }
  if (parts.length === 2) {
    return { host: parts[0], port: parts[1] };
  }
  return null; // голый хост — остальные поля не трогаем
};

const InlineAddProxy = ({ isPhoneView = false, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "socks5",
    host: "",
    port: "",
    username: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const formatMs = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return "—";
    return tf`${Math.round(parsed)}мс`;
  };

  const patch = (next) => {
    setFormData((prev) => ({ ...prev, ...next }));
    setTestResult(null);
  };

  const applyParsedProxy = (parsed, rawValue) => {
    if (parsed && parsed.port) {
      patch({
        host: parsed.host,
        port: parsed.port,
        username: parsed.username !== undefined ? parsed.username : formData.username,
        password: parsed.password !== undefined ? parsed.password : formData.password
      });
      return true;
    }
    patch({ host: rawValue });
    return false;
  };

  // Ввод в «Адрес»: если вставлена полная строка — раскладываем по полям.
  const handleHostChange = (value) => {
    const looksFull = /[@]|:\/\//.test(value) || value.includes(":");
    applyParsedProxy(looksFull ? parseProxyString(value) : null, value);
  };

  // Вставка обрабатывается отдельно: берём текст напрямую из буфера обмена, а не
  // из значения поля. Мобильные браузеры прогоняют вставляемый текст через
  // автокоррекцию и могут изменить регистр — тогда разобранный логин приезжал
  // в нижнем регистре. clipboardData отдаёт строку ровно как она есть.
  const handleHostPaste = (event) => {
    const raw = String(event.clipboardData?.getData("text") || "");
    if (!raw.trim()) return;
    const looksFull = /[@]|:\/\//.test(raw) || raw.includes(":");
    if (!looksFull) return;
    const parsed = parseProxyString(raw.trim());
    if (!parsed || !parsed.port) return;
    event.preventDefault();
    applyParsedProxy(parsed, raw.trim());
  };

  const testProxy = async () => {
    if (!formData.host || !formData.port) {
      alert(t("Заполните адрес и порт прокси"));
      return;
    }
    setLoading(true);
    setTestResult(null);
    const payload = {
      type: formData.type,
      host: formData.host,
      port: formData.port,
      username: formData.username,
      password: formData.password
    };
    try {
      // Test only — this endpoint does NOT create a proxy record.
      const data = await apiFetchJson("/api/proxies/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      setTestResult(data);
    } catch (error) {
      const backendResult = error?.data && typeof error.data === "object" ? error.data : {};
      const backendError = backendResult.error || error?.message || t("Ошибка при тестировании прокси");
      setTestResult({
        success: false,
        ...backendResult,
        error: backendError,
        status: error?.status || null
      });
    } finally {
      setLoading(false);
    }
  };

  // Persist the proxy (creates the record) only when the user clicks «Добавить».
  const addProxy = async () => {
    if (!testResult || !testResult.success) {
      alert(t("Сначала протестируйте прокси"));
      return;
    }
    if (saving) return;
    setSaving(true);
    const payload = {
      ...formData,
      name: formData.name || `${formData.host}:${formData.port}`
    };
    try {
      const data = await apiFetchJson("/api/proxies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (data && data.success && data.proxy) {
        if (onSuccess) onSuccess(data.proxy);
        setFormData({ name: "", type: "socks5", host: "", port: "", username: "", password: "" });
        setTestResult(null);
      } else {
        setTestResult({
          success: false,
          ...(data && typeof data === "object" ? data : {}),
          error: (data && data.error) || t("Не удалось добавить прокси")
        });
      }
    } catch (error) {
      const backendResult = error?.data && typeof error.data === "object" ? error.data : {};
      setTestResult({
        success: false,
        ...backendResult,
        error: backendResult.error || error?.message || t("Не удалось добавить прокси"),
        status: error?.status || null
      });
    } finally {
      setSaving(false);
    }
  };

  const clearForm = () => {
    setFormData({ name: "", type: "socks5", host: "", port: "", username: "", password: "" });
    setTestResult(null);
  };

  // ── стили ──
  const cardStyle = {
    borderRadius: "18px",
    border: "1px solid rgba(141,147,163,0.16)",
    background: S.surface,
    padding: "18px 18px 16px"
  };
  const labelStyle = {
    fontSize: "11px",
    fontWeight: 600,
    color: "#8d93a3",
    marginBottom: "6px"
  };
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    height: "44px",
    borderRadius: "12px",
    background: "rgba(8,9,12,0.6)",
    border: "1px solid rgba(141,147,163,0.3)",
    padding: "0 13px",
    fontSize: "13.5px",
    color: "#e2e4e9",
    outline: "none",
    fontFamily: "'Manrope', sans-serif",
    transition: "all 0.2s ease"
  };
  const onInputFocus = (e) => {
    e.currentTarget.style.borderColor = aa(400, 0.55);
    e.currentTarget.style.boxShadow = `0 0 0 3px ${aa(500, 0.14)}`;
  };
  const onInputBlur = (e) => {
    e.currentTarget.style.borderColor = "rgba(141,147,163,0.3)";
    e.currentTarget.style.boxShadow = "none";
  };
  const ghostBtn = {
    padding: "0 18px",
    height: "44px",
    color: "#d5d8e0",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "transparent",
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 600,
    fontSize: "13.5px",
    fontFamily: "'Manrope', sans-serif",
    transition: "all 0.2s ease"
  };
  const primaryBtn = (enabled) => ({
    padding: "0 20px",
    height: "44px",
    color: onAccent("#fff"),
    border: "none",
    background: A[500],
    borderRadius: "12px",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.6,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: "'Manrope', sans-serif",
    transition: "all 0.2s ease"
  });

  // On mobile the three action buttons must share the row width (otherwise
  // «Добавить» overflows off-screen), so let them flex and wrap.
  const btnMobile = isPhoneView
    ? { flex: "1 1 auto", minWidth: 0, padding: "0 10px", justifyContent: "center" }
    : {};
  const success = Boolean(testResult?.success);
  const check = testResult?.checkResult || {};
  const locText = check.location
    ? [check.location.country, check.location.city].filter(Boolean).join(" · ")
    : "";

  return (
    <div style={cardStyle}>
      {/* заголовок */}
      <div style={{ display: "flex", alignItems: "center", gap: "9px", marginBottom: "14px" }}>
        <PlusIcon size={16} color="#8d93a3" />
        <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: "14px", color: "#e2e4e9", letterSpacing: "0.01em" }}>
          {t("Новый прокси")}
        </span>
        <span style={{
          fontSize: "12px", fontWeight: 600, color: "#8d93a3", letterSpacing: "0.02em",
          background: "rgba(141,147,163,0.1)", border: "1px solid rgba(141,147,163,0.2)",
          borderRadius: "7px", padding: "3px 8px", marginLeft: "2px"
        }}>
          SOCKS5
        </span>
      </div>

      {/* поля */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isPhoneView ? "1fr 1fr" : "minmax(0,1.7fr) 108px minmax(0,1.1fr) minmax(0,1.1fr)",
        gap: "12px"
      }}>
        <div style={{ gridColumn: isPhoneView ? "1 / -1" : "auto" }}>
          <div style={labelStyle}>{t("Адрес / вставьте прокси целиком")}</div>
          <input
            value={formData.host}
            onChange={(e) => handleHostChange(e.target.value)}
            placeholder="host  ·  socks5://user:pass@host:port"
            spellCheck={false}
            autoComplete="off"
            // Mobile keyboards autocapitalise/autocorrect by default and mangle a
            // pasted proxy string — which silently lower-cased credentials that
            // are parsed out of this field. Keep the value verbatim.
            autoCapitalize="off"
            autoCorrect="off"
            type="text"
            inputMode="text"
            style={inputStyle}
            onPaste={handleHostPaste}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
        </div>
        <div>
          <div style={labelStyle}>{t("Порт")}</div>
          <input
            value={formData.port}
            onChange={(e) => patch({ port: e.target.value })}
            placeholder="8080"
            spellCheck={false}
            autoComplete="off"
            style={inputStyle}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
        </div>
        <div>
          <div style={labelStyle}>{t("Логин")}</div>
          <input
            value={formData.username}
            onChange={(e) => patch({ username: e.target.value })}
            type="text"
            placeholder="—"
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            name="kl-px-a"
            data-lpignore="true"
            data-1p-ignore=""
            data-form-type="other"
            style={inputStyle}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
        </div>
        <div>
          <div style={labelStyle}>{t("Пароль")}</div>
          {/* type="text" (not password): shows the proxy password, and — crucially —
              stops browsers/password managers from treating this as a login form
              and autofilling the panel's own account credentials. */}
          <input
            value={formData.password}
            onChange={(e) => patch({ password: e.target.value })}
            type="text"
            placeholder="—"
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            name="kl-px-b"
            data-lpignore="true"
            data-1p-ignore=""
            data-form-type="other"
            style={inputStyle}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
        </div>
      </div>

      {/* подсказка форматов */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", fontSize: "12px", color: "#707684" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8d93a3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />
        </svg>
        <span>
          {t("Форматы распознаются автоматически:")}{" "}
          <span style={{ color: "#8d93a3", fontVariantNumeric: "tabular-nums" }}>socks5://user:pass@host:port</span> ·{" "}
          <span style={{ color: "#8d93a3", fontVariantNumeric: "tabular-nums" }}>host:port:user:pass</span> ·{" "}
          <span style={{ color: "#8d93a3", fontVariantNumeric: "tabular-nums" }}>host:port</span>
        </span>
      </div>

      {/* результат + кнопки */}
      <div style={{
        display: "flex",
        alignItems: isPhoneView ? "stretch" : "center",
        justifyContent: "space-between",
        gap: "16px",
        marginTop: "14px",
        flexDirection: isPhoneView ? "column" : "row"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, fontSize: "13px", color: "#8d93a3", flexWrap: "wrap" }}>
          {testResult && success && (
            <>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", color: "#6ee7b7", fontWeight: 600 }}>
                <CheckIcon size={15} color="#34d399" /> {t("Работает")}
              </span>
              <span style={{ color: "#3f4450" }}>·</span>
              <span style={{ color: "#fbbf24", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{formatMs(check.responseTime)}</span>
              {check.ip && (<><span style={{ color: "#3f4450" }}>·</span><span style={{ color: "#c6c9d1", fontVariantNumeric: "tabular-nums" }}>{check.ip}</span></>)}
              {locText && (<><span style={{ color: "#3f4450" }}>·</span><span style={{ color: "#c6c9d1" }}>{locText}</span></>)}
              {check.isp && (<><span style={{ color: "#3f4450" }}>·</span><span style={{ color: "#c6c9d1", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{check.isp}</span></>)}
            </>
          )}
          {testResult && !success && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", color: "#fca5a5", fontWeight: 600 }}>
              <XIcon size={15} color="#f87171" /> {testResult.error || testResult.checkResult?.error || t("Прокси не работает")}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, width: isPhoneView ? "100%" : "auto", flexWrap: isPhoneView ? "wrap" : "nowrap" }}>
          <button
            type="button"
            onClick={clearForm}
            style={{ ...ghostBtn, color: "#8d93a3", border: "1px solid rgba(141,147,163,0.22)", ...btnMobile }}
          >
            {t("Очистить")}
          </button>
          {/* Icon and label live in fixed <span> slots, and the button gets its own
              compositing layer. Swapping a bare text node next to the animated
              spinner left a stale repaint on mobile — the old label stayed painted
              under the new one, so the caption looked doubled. */}
          <button
            type="button"
            onClick={testProxy}
            disabled={loading || !formData.host || !formData.port}
            style={{
              ...ghostBtn,
              opacity: (loading || !formData.host || !formData.port) ? 0.6 : 1,
              ...btnMobile,
              transform: "translateZ(0)",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden"
            }}
          >
            <span style={{ display: "inline-flex", width: "16px", height: "16px", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {loading ? (
                <Spinner size={15} />
              ) : (
                <RefreshIcon size={16} />
              )}
            </span>
            <span>{loading ? t("Проверка...") : t("Проверить")}</span>
          </button>
          <button
            type="button"
            onClick={addProxy}
            disabled={!success || saving}
            style={{
              ...primaryBtn(success && !saving),
              ...btnMobile,
              transform: "translateZ(0)",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden"
            }}
          >
            <span style={{ display: "inline-flex", width: "16px", height: "16px", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {saving ? (
                <Spinner size={15} />
              ) : (
                <SendIcon size={16} />
              )}
            </span>
            <span>{saving ? t("Добавление...") : t("Добавить")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InlineAddProxy;
